import React, { useState } from 'react';
import { VALID_CATEGORIES } from '../constants';

/**
 * FilterPanel - Category and tag filtering controls for the timeline.
 * Provides category filter buttons and a tag input for filtering displayed events.
 *
 * @param {object} props
 * @param {function} props.onFilterChange - Callback when filters change: ({ category, tags }) => void
 * @param {string[]} props.availableTags - Array of all available tags to select from
 * @param {{ category: string|null, tags: string[] }} props.activeFilters - Current active filters
 */
function FilterPanel({ onFilterChange, availableTags = [], activeFilters = { category: null, tags: [] } }) {
  const [tagInput, setTagInput] = useState('');

  const handleCategoryClick = (category) => {
    const newCategory = category === activeFilters.category ? null : category;
    onFilterChange({ category: newCategory, tags: activeFilters.tags });
  };

  const handleAllClick = () => {
    onFilterChange({ category: null, tags: activeFilters.tags });
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const tag = tagInput.trim();
      if (tag && !activeFilters.tags.includes(tag)) {
        onFilterChange({ category: activeFilters.category, tags: [...activeFilters.tags, tag] });
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = activeFilters.tags.filter((t) => t !== tagToRemove);
    onFilterChange({ category: activeFilters.category, tags: newTags });
  };

  return (
    <div className="filter-panel" role="group" aria-label="Event filters">
      <div className="filter-panel-categories">
        <span className="filter-panel-label">Category:</span>
        <button
          className={`filter-btn ${activeFilters.category === null ? 'filter-btn-active' : ''}`}
          onClick={handleAllClick}
          aria-pressed={activeFilters.category === null}
        >
          All
        </button>
        {VALID_CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`filter-btn ${activeFilters.category === cat ? 'filter-btn-active' : ''}`}
            onClick={() => handleCategoryClick(cat)}
            aria-pressed={activeFilters.category === cat}
          >
            {cat.replace('_', ' ')}
          </button>
        ))}
      </div>

      <div className="filter-panel-tags">
        <span className="filter-panel-label">Tags:</span>
        <input
          type="text"
          className="filter-tag-input"
          placeholder="Add tag filter..."
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagInputKeyDown}
          aria-label="Tag filter input"
          list="available-tags-list"
        />
        <datalist id="available-tags-list">
          {availableTags
            .filter((tag) => !activeFilters.tags.includes(tag))
            .map((tag) => (
              <option key={tag} value={tag} />
            ))}
        </datalist>

        {activeFilters.tags.length > 0 && (
          <div className="filter-active-tags" aria-label="Active tag filters">
            {activeFilters.tags.map((tag) => (
              <span key={tag} className="filter-tag-chip">
                {tag}
                <button
                  className="filter-tag-remove"
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Remove tag ${tag}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FilterPanel;
